import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BASE_END_POINT_COMPANY, COMPANY_END_POINT } from '../../../config';
import { getLocalStorageAuthentication } from '../../../lib/auth';
import { messageUpdated } from '../app/appSlice';
import { Severity } from '../../../components/Message/Message';
import { createCompanySucceeded, getUserCompanySucceeded } from '../company/companiesSlice';
import { saveDraftCompanySession } from '../../../lib/sessionStorage';

export const companyApi = createApi({
  reducerPath: 'companyApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_END_POINT_COMPANY}`
  }),
  tagTypes: ['Company'],
  endpoints: (build) => ({
    findCompanyByUserId: build.query({
      query: ({ userId, isPublic = false }) => {
        const userIdEncoded = encodeURIComponent(userId);
        const headers = {};
        
        // Only add authorization header if not a public request
        if (!isPublic) {
          const localAuth = getLocalStorageAuthentication();
          if (localAuth && localAuth.token) {
            headers.Authorization = `Bearer ${localAuth.token}`;
          }
        }
        
        return {
          url: `${COMPANY_END_POINT}/user/${userIdEncoded}`,
          method: 'GET',
          headers
        };
      },
      providesTags: (result, error, arg) => 
        result?.data ? [{ type: 'Company', id: arg.userId }] : [],
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;
          console.log('✅ [Company API] Company fetched successfully:', data);
          
          // Only dispatch to Redux if NOT a public request
          // Public requests use data directly from the hook
          if (!arg.isPublic) {
          // Dispatch company to Redux store
          const company = Array.isArray(data?.data) ? data.data[0] : data?.data;
          if (company && company.id) {
            console.log('🏢 [Company API] Dispatching company to Redux');
            dispatch(getUserCompanySucceeded({ company }));
            }
          }
        } catch (error) {
          console.error('❌ [Company API] Failed to fetch company:', error);
        }
      }
    }),
    createCompany: build.mutation({
      query: ({ company }) => {
        const localAuth = getLocalStorageAuthentication();
        company.userId = localAuth.userId;
        company.id = null;
        company.createdAt = null;
        company.updatedAt = null;
        return {
          url: `${COMPANY_END_POINT}`,
          method: 'POST',
          headers: { Authorization: `Bearer ${localAuth.token}` },
          body: company
        };
      },
      invalidatesTags: (result, error, arg) => 
        result?.data ? [{ type: 'Company', id: arg.company.userId }] : [],
      // Effects to trigger on success
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        const errorMessage = () => {
          dispatch(
            messageUpdated({
              text: 'Company not created, please try again',
              severity: Severity.ERROR
            })
          );
        };
        try {
          const { data } = await queryFulfilled;
          // API may return Company directly or wrapped in another structure
          const company = data?.data?.company || data?.data;
          const isCompanyCreated = company && company.id;

          if (isCompanyCreated) {
            saveDraftCompanySession(company);
            dispatch(
              createCompanySucceeded({
                company
              })
            );
            dispatch(
              messageUpdated({
                text: 'Company created successfully',
                severity: Severity.SUCCESS
              })
            );
          } else {
            errorMessage();
          }
        } catch (error) {
          errorMessage();
        }
      }
    }),
    updateCompany: build.mutation({
      query: ({ company }) => {
        const localAuth = getLocalStorageAuthentication();
        return {
          url: `${COMPANY_END_POINT}`,
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${localAuth.token}`
          },
          body: company
        };
      },
      invalidatesTags: (result, error, arg) => 
        result?.data ? [{ type: 'Company', id: arg.company.userId }] : [],
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        const errorMessage = () => {
          dispatch(
            messageUpdated({
              text: 'Company not updated, please try again',
              severity: Severity.ERROR
            })
          );
        };
        try {
          const { data } = await queryFulfilled;
          // API may return Company directly or wrapped in another structure
          const company = data?.data?.company || data?.data;
          const isCompanyUpdated = company && company.id;

          if (isCompanyUpdated) {
            saveDraftCompanySession(company);
            dispatch(
              getUserCompanySucceeded({
                company
              })
            );
            dispatch(
              messageUpdated({
                text: 'Company updated successfully',
                severity: Severity.SUCCESS
              })
            );
          } else {
            errorMessage();
          }
        } catch (error) {
          errorMessage();
        }
      }
    })
  })
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { 
  useFindCompanyByUserIdQuery, 
  useCreateCompanyMutation,
  useUpdateCompanyMutation
} = companyApi;
